/**
 * Auto-save patches for the lesson editor's draft. Pure, so it can be tested
 * without Firebase (draftPatch.test.ts).
 *
 * An auto-save used to read the stored draft, upload every recording still
 * held in memory (all of this session's takes, on every save) and rewrite the
 * whole document. On a phone that took 22 to 42 seconds. Now a save whose
 * recordings are unchanged writes only the fields that changed, without the
 * read; a save that adds, removes or reorders a recording takes the full path,
 * which reads first so the server's clip fields and the empty-plan guard
 * still apply.
 */

export interface PatchSegment {
  id: string;
  label: string;
  startTime: number;
  endTime: number;
  note?: string;
}

export interface PatchClip {
  id: string;
  segmentIds: string[];
  isCameraSource?: boolean;
  videoDuration?: number;
  videoStoragePath?: string;
  thumbnailDataUrl?: string;
  label?: string;
  videoBlob?: Blob;
}

/** The editable fields of a draft, as the editor sends them and as they are stored. */
export interface PatchPlan {
  notableSegments: PatchSegment[];
  voiceoverNotableSegmentClips: PatchClip[];
  notes: string;
  audioMuted: boolean;
  coachNotes?: string;
  attachedDrills?: unknown[];
  videoRotation?: number;
}

/** A segment as it is stored: the editor's display-only fields are dropped. */
export function storedSegment(seg: PatchSegment): PatchSegment {
  return {
    id: seg.id,
    label: seg.label,
    startTime: seg.startTime,
    endTime: seg.endTime,
    ...(seg.note !== undefined && { note: seg.note }),
  };
}

/** A recording that exists only in memory: it must be uploaded, so it takes the full path. */
export function clipNeedsUpload(clip: PatchClip): boolean {
  return !!clip.videoBlob && !clip.videoStoragePath;
}

/** The clip fields this client owns (videoEditDraft's CLIENT_CLIP_KEYS), for comparison. */
function clientClipView(clip: PatchClip) {
  return {
    id: clip.id,
    segmentIds: clip.segmentIds,
    isCameraSource: clip.isCameraSource || undefined,
    videoDuration: clip.videoDuration,
    videoStoragePath: clip.videoStoragePath,
    thumbnailDataUrl: clip.thumbnailDataUrl || undefined,
    label: clip.label || undefined,
  };
}

/** JSON with sorted keys, undefined dropped: the same value always gives the same string. */
export function stableJson(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) out[k] = (v as Record<string, unknown>)[k];
      return out;
    }
    return v;
  });
}

/** True when the recordings, their order and their fields match what is stored, and none needs uploading. */
export function clipsUnchanged(incoming: PatchClip[], stored: PatchClip[]): boolean {
  if (incoming.length !== stored.length) return false;
  if (incoming.some(clipNeedsUpload)) return false;
  return stableJson(incoming.map(clientClipView)) === stableJson(stored.map(clientClipView));
}

const PLAIN_FIELDS = ['notes', 'audioMuted', 'coachNotes', 'attachedDrills', 'videoRotation'] as const;

/**
 * The fields to write when only plain fields changed, or null when the save
 * must take the full path: a recording changed, or a field this client used
 * to store is now absent (the full path's rewrite is what removes it).
 * An empty object means nothing changed.
 */
export function draftPatch(stored: PatchPlan, incoming: PatchPlan): Record<string, unknown> | null {
  if (!clipsUnchanged(incoming.voiceoverNotableSegmentClips, stored.voiceoverNotableSegmentClips ?? [])) return null;
  const patch: Record<string, unknown> = {};
  const segments = incoming.notableSegments.map(storedSegment);
  if (stableJson(segments) !== stableJson((stored.notableSegments ?? []).map(storedSegment))) patch.notableSegments = segments;
  for (const field of PLAIN_FIELDS) {
    const next = incoming[field];
    const prev = stored[field];
    if (next === undefined) {
      if (prev !== undefined) return null;
      continue;
    }
    if (stableJson(next) !== stableJson(prev)) patch[field] = next;
  }
  return patch;
}
